import './warehouse-filter.css';
import React, { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

function optionText(node) {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (!React.isValidElement(node)) return '';
  return React.Children.toArray(node.props.children).map(optionText).join(' ');
}

export default function FilterMenu({ open, onToggle, onClose, icon, label, value, ariaLabel, className = '', searchable = false, children }) {
  const ref = useRef(null);
  const inputRef = useRef(null);
  const listId = useId();
  const [query, setQuery] = useState('');
  const options = searchable ? React.Children.toArray(children).filter(child => optionText(child).toLowerCase().includes(query.trim().toLowerCase())) : children;
  useEffect(() => {
    if (!open) return undefined;
    function handlePointer(event) {
      if (!ref.current?.contains(event.target)) onClose();
    }
    function handleKey(event) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !searchable) return;
    setQuery('');
    inputRef.current?.focus({ preventScroll: true });
  }, [open, searchable]);

  function handleKeyDown(event) {
    if (event.key === 'Escape') { event.stopPropagation(); onClose(); inputRef.current?.blur(); return; }
    if (!open || !['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    event.preventDefault();
    const items = Array.from(ref.current?.querySelectorAll('[role="option"]') || []);
    if (!items.length) return;
    const current = items.indexOf(document.activeElement);
    const next = current < 0 ? (event.key === 'ArrowDown' ? 0 : items.length - 1) :
      (current + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
    items[next].focus();
  }

  return <div className={`wh-menu ${className}`} ref={ref} onClick={event => {
    if (searchable && !event.target.closest('input')) inputRef.current?.focus({ preventScroll: true });
  }}>
    {searchable ? <div className={`wh-menu-trigger${open ? ' open' : ''}`} onKeyDown={handleKeyDown}>
      {icon}
      {label && <span className="wh-menu-label">{label}</span>}
      <input ref={inputRef} className="wh-menu-search-input" type="text" role="combobox" aria-label={ariaLabel || `${label} filter`} aria-controls={listId} aria-haspopup="listbox" aria-expanded={open} aria-autocomplete="list" autoComplete="off" spellCheck="false"
        value={open ? query : value} placeholder={open ? `Search ${label.toLowerCase()}...` : undefined}
        onFocus={() => { if (!open) { setQuery(''); onToggle(); } }}
        onClick={() => { if (!open) { setQuery(''); onToggle(); } }}
        onChange={event => { setQuery(event.target.value); if (!open) onToggle(); }} />
      <ChevronDown size={14} strokeWidth={1.8} className="wh-menu-caret" />
    </div> : <button className={`wh-menu-trigger${open ? ' open' : ''}`} type="button" aria-label={ariaLabel} aria-haspopup="listbox" aria-expanded={open} onClick={onToggle}>
      {icon}
      {label && <span className="wh-menu-label">{label}</span>}
      <span className="wh-menu-value">{value}</span>
      <ChevronDown size={14} strokeWidth={1.8} className="wh-menu-caret" />
    </button>}
    {open && <div className="wh-menu-panel" id={listId} role="listbox">{options}{searchable && !options.length && <p className="wh-menu-empty">No matching {label.toLowerCase()}.</p>}</div>}
  </div>;
}

